import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { INDIAN_STATES } from "@/lib/statesData";
import { ArrowUpRight, MapPin, Building2 } from "lucide-react";
import SearchBar from "@/components/SearchBar";

export default function Home() {
  const [data, setData] = useState({
    total_hotels: 0, total_cities: 0, total_states: 0,
    operational_hotels: 0, upcoming_hotels: 0,
    states: [],
  });
  const [disabledStates, setDisabledStates] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.summary(), api.getDisabledStates()])
      .then(([sum, ds]) => {
        setData(sum);
        setDisabledStates(new Set(ds.states || []));
      })
      .finally(() => setLoading(false));
  }, []);

  const stateStatsMap = Object.fromEntries(data.states.map((s) => [s.state, s]));
  const activeStates = new Set(data.states.map((s) => s.state));
  const allStates = INDIAN_STATES.filter((name) => !disabledStates.has(name)).map((name) => ({
    state: name,
    hotel_count: stateStatsMap[name]?.hotel_count || 0,
    city_count: stateStatsMap[name]?.city_count || 0,
    upcoming: stateStatsMap[name]?.upcoming || 0,
    rooms: stateStatsMap[name]?.rooms || 0,
    active: activeStates.has(name),
  }));
  allStates.sort((a, b) => (a.active !== b.active ? (a.active ? -1 : 1) : a.state.localeCompare(b.state)));

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-[var(--line)] overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1566073771259-6a8506099945?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.82) 45%, rgba(255,255,255,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-24 sm:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <div className="md:col-span-8">
              <div className="font-label text-xs text-[var(--brand-blue)] mb-4 sm:mb-6">
                <span className="inline-block w-8 h-[2px] bg-[var(--brand-blue)] mr-3 align-middle" />
                Royal Orchid Hotels · Across India
              </div>
              <h1
                data-testid="hero-title"
                className="font-display text-4xl sm:text-6xl lg:text-8xl leading-[0.9] text-[var(--ink)]"
              >
                A Nation of<br />
                <span className="text-[var(--brand-blue)]">Hospitality.</span><br />
                One Portfolio.
              </h1>
              <p className="mt-6 sm:mt-8 text-base sm:text-lg text-[var(--ink-soft)] max-w-2xl leading-relaxed">
                Explore the world of <span className="text-[var(--ink)] font-semibold">Royal Orchid Hotels</span> across India.
                Browse by <span className="text-[var(--ink)] font-semibold">state</span>,{" "}
                <span className="text-[var(--ink)] font-semibold">city</span>, or hotel to discover destinations crafted for every journey.
              </p>
              <div className="mt-6 sm:mt-10 flex items-center gap-3 flex-wrap">
                <Link
                  to="/map"
                  data-testid="home-open-map"
                  className="inline-flex items-center gap-2 bg-[var(--ink)] text-white px-5 py-3 font-display text-xs sm:text-sm hover:bg-[var(--brand-blue)] transition-colors"
                >
                  Open Interactive Map <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/browse/hotels"
                  data-testid="home-browse"
                  className="inline-flex items-center gap-2 border-2 border-[var(--ink)] px-5 py-3 font-display text-xs sm:text-sm hover:bg-[var(--ink)] hover:text-white transition-colors"
                >
                  Browse Directory
                </Link>
              </div>
            </div>

            <div className="md:col-span-4 grid grid-cols-3 md:grid-cols-1 gap-2 sm:gap-3">
              <StatBox testId="stat-hotels" label="Hotels" value={data.total_hotels} accent="bg-[var(--brand-blue)]" />
              <StatBox testId="stat-cities" label="Cities" value={data.total_cities} accent="bg-[var(--brand-orange)]" />
              <StatBox testId="stat-states" label="States" value={data.total_states} accent="bg-[var(--brand-yellow)]" ink />
            </div>
          </div>
        </div>

        <div className="relative border-t border-[var(--line)] bg-[var(--ink)] text-white overflow-hidden">
          <div className="marquee-track flex whitespace-nowrap py-3">
            {Array.from({ length: 2 }).flatMap((_, i) =>
              ["ROYAL ORCHID", "HOSPITALITY", "PORTFOLIO", "PAN-INDIA", "HERITAGE", "LUXURY", "EST. 2026"].map((t, j) => (
                <span key={`${i}-${j}`} className="font-display text-xl sm:text-2xl mx-6 sm:mx-8 tracking-tight">
                  {t} <span className="text-[var(--brand-yellow)] mx-2 sm:mx-4">✦</span>
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* SEARCH */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16">
        <div className="mb-6 max-w-3xl">
          <div className="font-label text-xs text-[var(--ink-soft)] mb-3">Section 01 / Search</div>
          <h2 className="font-display text-3xl sm:text-5xl tracking-tight mb-6">Find anything, fast.</h2>
          <SearchBar />
          <p className="mt-3 text-xs text-[var(--ink-soft)]">
            Searches across hotel names, locations, cities and states.
          </p>
        </div>
      </section>

      {/* STATES GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-24">
        <div className="flex items-end justify-between mb-8 sm:mb-10 gap-4 flex-wrap">
          <div>
            <div className="font-label text-xs text-[var(--ink-soft)] mb-3">Section 02 / Directory</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight">Choose a State.</h2>
          </div>
          <p className="text-[var(--ink-soft)] max-w-md text-sm">
            Active states show live hotel counts. Click any state to see its cities, hotels and a state map.
          </p>
        </div>

        {loading ? (
          <div data-testid="states-loading" className="text-[var(--ink-soft)] font-label text-sm">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {allStates.map((s, idx) => <StateCard key={s.state} s={s} idx={idx} />)}
          </div>
        )}

        {data.total_hotels === 0 && !loading && (
          <div data-testid="empty-hotels" className="mt-12 sm:mt-16 border-2 border-dashed border-[var(--line)] p-6 sm:p-10 text-center">
            <div className="font-label text-xs text-[var(--brand-blue)] mb-3">Empty · Awaiting Hotels</div>
            <h3 className="font-display text-2xl sm:text-3xl mb-3">No hotels yet.</h3>
            <p className="text-[var(--ink-soft)] mb-6 max-w-md mx-auto text-sm">
              Sign in as admin and start adding your properties. States will light up automatically as they&rsquo;re added.
            </p>
            <Link
              to="/admin/login"
              data-testid="empty-admin-cta"
              className="inline-flex items-center gap-2 bg-[var(--ink)] text-white px-6 py-3 font-display text-xs sm:text-sm hover:bg-[var(--brand-blue)] transition-colors"
            >
              Go to Admin <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

function StatBox({ label, value, accent, ink, testId }) {
  return (
    <div
      data-testid={testId}
      className={`${accent} ${ink ? "text-[var(--ink)]" : "text-white"} p-3 sm:p-6 flex flex-col justify-between min-h-[80px] sm:min-h-[100px]`}
    >
      <div className="font-label text-[10px] opacity-90">{label}</div>
      <div className="font-display text-3xl sm:text-5xl md:text-6xl leading-none mt-2">{value}</div>
    </div>
  );
}

function StateCard({ s, idx }) {
  const inactive = !s.active;
  return (
    <Link
      to={`/state/${encodeURIComponent(s.state)}`}
      data-testid={`state-card-${slug(s.state)}`}
      className={`group fade-up block border p-4 sm:p-6 transition-all duration-300 ${
        inactive
          ? "border-[var(--line)] bg-[var(--paper-2)] hover:border-[var(--ink)]"
          : "border-[var(--ink)] bg-white hover:-translate-y-[2px] hover:shadow-[8px_8px_0px_0px_var(--brand-blue)]"
      }`}
      style={{ animationDelay: `${Math.min(idx * 30, 500)}ms` }}
    >
      <div className="flex items-start justify-between mb-4 sm:mb-6">
        <span className="font-label text-[10px] text-[var(--ink-soft)]">{String(idx + 1).padStart(2, "0")}</span>
        {s.active ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-label text-[var(--brand-blue)]">
            <span className="w-1.5 h-1.5 bg-[var(--brand-blue)]" /> Active
          </span>
        ) : (
          <span className="text-[10px] font-label text-[var(--ink-soft)]">—</span>
        )}
      </div>
      <h3 className="font-display text-lg sm:text-2xl leading-tight mb-4 sm:mb-6 group-hover:text-[var(--brand-blue)] transition-colors">
        {s.state}
      </h3>
      {s.active && (
        <div className="flex items-center gap-4 mb-4">
          <div>
            <div className="font-display text-2xl leading-none">{s.hotel_count}</div>
            <div className="font-label text-[9px] text-[var(--ink-soft)] mt-0.5">Hotels</div>
          </div>
          <div>
            <div className="font-display text-2xl leading-none text-[var(--brand-blue)]">{s.rooms}</div>
            <div className="font-label text-[9px] text-[var(--ink-soft)] mt-0.5">Rooms</div>
          </div>
          {s.city_count > 0 && (
            <div>
              <div className="font-display text-2xl leading-none">{s.city_count}</div>
              <div className="font-label text-[9px] text-[var(--ink-soft)] mt-0.5">Cities</div>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between text-[11px] sm:text-xs">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {!s.active && (
            <>
              <span className="inline-flex items-center gap-1 text-[var(--ink-soft)]">
                <Building2 className="w-3 h-3" /> 0
              </span>
              <span className="inline-flex items-center gap-1 text-[var(--ink-soft)]">
                <MapPin className="w-3 h-3" /> 0
              </span>
            </>
          )}
          {s.upcoming > 0 && (
            <span className="inline-flex items-center gap-1 text-[var(--brand-orange)] font-semibold">
              +{s.upcoming} upcoming
            </span>
          )}
        </div>
        <ArrowUpRight className="w-4 h-4 text-[var(--ink)] group-hover:text-[var(--brand-blue)] transition-colors" />
      </div>
    </Link>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
